import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import PageContentLayout from '@/components/layout/PageContentLayout/PageContentLayout.js';
import VideoPreview from '@/components/media/VideoPreview.js';
import { getVideoTypes } from '@/features/systemManagement/videoType/videoTypeService.js';
import {
  createJianyingTemplate,
  deleteJianyingTemplate,
  fetchJianyingTemplateAvailability,
  fetchJianyingTemplates,
  uploadJianyingTemplatePreview,
} from '../../services/jianyingTemplateService.js';
import { fetchJianyingDevices } from '../../services/jianyingDeviceService.js';
import {
  JIANYING_TEMPLATE_RUNTIME_STATUS,
  JIANYING_TEMPLATE_STATUS,
  getJianyingStatusTone,
  getJianyingTemplateStatusLabel,
  shouldCheckJianyingTemplateAvailability,
} from '../../constants/jianyingTemplateStatus.js';
import { getJianyingServiceStatus } from '../../utils/jianyingDevices.js';
import JianyingTemplatePreviewField from './components/JianyingTemplatePreviewField.js';
import '../../../../jianying/JianyingCommon.scss';
import '../../JianyingTemplates.scss';

const TEMPLATE_LIST_PATH = '/system-management/jianying/templates';

const EMPTY_FORM = {
  name: '',
  draft_name: '',
  is_active: true,
  template_preview: null,
  video_type_ids: [],
};

const getVideoTypeLabel = (videoType) => (
  videoType.title || videoType.videoType || videoType.name || `视频类型 ${videoType.id}`
);

const errorMessage = (error, fallback) => (
  error.response?.data?.message || error.message || fallback
);

const getTemplateRuntimeStatus = (template, availability) => (
  availability[template.id]?.status
  || (shouldCheckJianyingTemplateAvailability(template.status)
    ? JIANYING_TEMPLATE_RUNTIME_STATUS.CHECKING
    : undefined)
);

const JianyingTemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [videoTypes, setVideoTypes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [serviceStatus, setServiceStatus] = useState(null);
  const [templateAvailability, setTemplateAvailability] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [previewFile, setPreviewFile] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const availabilityRequestId = useRef(0);

  const refreshTemplateAvailability = useCallback(async (templateData, deviceData) => {
    const requestId = availabilityRequestId.current + 1;
    availabilityRequestId.current = requestId;
    const checkableIds = templateData
      .filter((template) => shouldCheckJianyingTemplateAvailability(template.status))
      .map((template) => template.id);
    setTemplateAvailability(Object.fromEntries(checkableIds.map((id) => [id, {
      template_id: id,
      status: JIANYING_TEMPLATE_RUNTIME_STATUS.CHECKING,
    }])));
    setServiceStatus(getJianyingServiceStatus(deviceData));
    if (!checkableIds.length) return;
    try {
      const items = await fetchJianyingTemplateAvailability(checkableIds);
      if (availabilityRequestId.current !== requestId) return;
      const byId = new Map(items.map((item) => [item.template_id, item]));
      setTemplateAvailability(Object.fromEntries(checkableIds.map((id) => [
        id,
        byId.get(id) || {
          template_id: id,
          status: JIANYING_TEMPLATE_RUNTIME_STATUS.UNREADABLE,
        },
      ])));
    } catch {
      if (availabilityRequestId.current !== requestId) return;
      setTemplateAvailability(Object.fromEntries(checkableIds.map((id) => [id, {
        template_id: id,
        status: JIANYING_TEMPLATE_RUNTIME_STATUS.SERVICE_OFFLINE,
      }])));
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const [templateData, videoTypeData, deviceData] = await Promise.all([
        fetchJianyingTemplates(statusFilter ? { status: statusFilter } : {}),
        getVideoTypes(),
        fetchJianyingDevices(),
      ]);
      setTemplates(templateData);
      setVideoTypes(videoTypeData || []);
      void refreshTemplateAvailability(templateData, deviceData);
    } catch (error) {
      setMessage(errorMessage(error, '读取剪映模板失败'));
    } finally {
      setLoading(false);
    }
  }, [refreshTemplateAvailability, statusFilter]);

  useEffect(() => { void loadData(); }, [loadData]);

  const videoTypeNameMap = useMemo(
    () => new Map(videoTypes.map((item) => [Number(item.id), getVideoTypeLabel(item)])),
    [videoTypes],
  );

  const openCreate = () => {
    setForm({ ...EMPTY_FORM });
    setPreviewFile(null);
    setMessage('');
    setShowCreate(true);
  };

  const openEdit = (template) => {
    navigate(`${TEMPLATE_LIST_PATH}/${template.id}`);
  };

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const toggleVideoType = (videoTypeId) => {
    const id = Number(videoTypeId);
    setForm((current) => ({
      ...current,
      video_type_ids: current.video_type_ids.includes(id)
        ? current.video_type_ids.filter((value) => value !== id)
        : [...current.video_type_ids, id],
    }));
  };

  const createTemplate = async () => {
    if (!form.name.trim() || !form.draft_name.trim()) {
      setMessage('模板名称和剪映草稿名不能为空');
      return;
    }
    if (!form.video_type_ids.length) {
      setMessage('请至少选择一个适用视频类型');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const previewUpload = previewFile
        ? await uploadJianyingTemplatePreview(previewFile)
        : null;
      const template = await createJianyingTemplate({
        name: form.name.trim(),
        draft_name: form.draft_name.trim(),
        is_active: form.is_active,
        video_type_ids: form.video_type_ids,
        ...(previewUpload?.object_key
          ? { template_preview_object_key: previewUpload.object_key }
          : {}),
      });
      setShowCreate(false);
      navigate(`${TEMPLATE_LIST_PATH}/${template.id}`);
    } catch (error) {
      setMessage(errorMessage(error, '创建或扫描剪映模板失败'));
    } finally {
      setBusy(false);
    }
  };

  const removeTemplate = async () => {
    if (!deleteCandidate) return;
    setBusy(true);
    setMessage('');
    try {
      await deleteJianyingTemplate(deleteCandidate.id);
      setDeleteCandidate(null);
      await loadData();
    } catch (error) {
      setMessage(errorMessage(error, '删除剪映模板失败'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageContentLayout
      pageClassName="jianying-workspace"
      title="剪映模板"
      description="管理模板并进入独立配置页维护槽位、分组和文案模板。"
      actions={<Button onClick={openCreate}><i className="bi bi-plus-lg" /> 新建模板</Button>}
      filters={(
        <div className="jianying-toolbar">
          <Form.Select aria-label="模板状态" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="">全部状态</option>
            {Object.values(JIANYING_TEMPLATE_STATUS).map((status) => (
              <option key={status} value={status}>{getJianyingTemplateStatusLabel(status)}</option>
            ))}
          </Form.Select>
          <span className={`jianying-status jianying-status--${serviceStatus?.tone || 'muted'}`}>
            模板服务：{serviceStatus?.label || '检查中'}
          </span>
        </div>
      )}
    >
      {message && !showCreate ? <p className="jianying-notice" role="alert">{message}</p> : null}
      <div className="jianying-table-wrap mt-3">
        <Table hover responsive={false}>
          <thead className="table-light"><tr>
            <th>模板</th><th>预览</th><th>状态</th><th>视频类型</th><th>槽位</th><th>文案</th><th>启用</th><th className="text-end">操作</th>
          </tr></thead>
          <tbody>
            {templates.map((template) => (
              <tr key={template.id} className="jianying-template-list-row" onDoubleClick={() => openEdit(template)}>
                <td><div className="jianying-template-name"><strong>{template.name}</strong><span>{template.draft_name}</span></div></td>
                <td>{template.template_preview ? (
                  <VideoPreview
                    ariaLabel={`播放 ${template.name} 预览视频`}
                    className="jianying-template-preview-thumb"
                    overlay={<span className="jianying-template-preview-thumb__play" aria-hidden="true"><i className="bi bi-play-fill" /></span>}
                    src={template.template_preview}
                    title={template.name}
                  />
                ) : '—'}</td>
                <td><span className={`jianying-status jianying-status--${getJianyingStatusTone(template.status, getTemplateRuntimeStatus(template, templateAvailability))}`}>
                  {getJianyingTemplateStatusLabel(template.status, getTemplateRuntimeStatus(template, templateAvailability), templateAvailability[template.id])}
                </span></td>
                <td>{(template.video_type_ids || []).map((id) => videoTypeNameMap.get(Number(id)) || id).join('、') || '—'}</td>
                <td>{(template.slots || []).filter((slot) => slot.replace_enabled !== false).length}</td>
                <td>{template.copy_presets?.length || 0} 套</td>
                <td>{template.is_active ? '是' : '否'}</td>
                <td><div className="d-flex justify-content-end gap-2">
                  <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => openEdit(template)}><i className="bi bi-box-arrow-in-right" /> 进入配置</Button>
                  <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => setDeleteCandidate(template)} title="删除模板"><i className="bi bi-trash3" /></Button>
                </div></td>
              </tr>
            ))}
            {!loading && !templates.length ? <tr><td colSpan="8" className="jianying-empty">暂无剪映模板</td></tr> : null}
            {loading ? <tr><td colSpan="8" className="jianying-empty"><Spinner size="sm" /> 正在读取模板</td></tr> : null}
          </tbody>
        </Table>
      </div>

      <Modal show={showCreate} onHide={() => !busy && setShowCreate(false)} centered className="jianying-template-create-modal">
        <Modal.Header closeButton><Modal.Title>新增剪映模板</Modal.Title></Modal.Header>
        <Modal.Body>
          {message ? <p className="jianying-notice" role="alert">{message}</p> : null}
          <div className="jianying-template-create-form">
            <Form.Group controlId="jianying-new-template-name">
              <Form.Label>模板名称</Form.Label>
              <Form.Control value={form.name} onChange={(event) => setField('name', event.target.value)} disabled={busy} />
            </Form.Group>
            <Form.Group controlId="jianying-new-template-draft-name">
              <Form.Label>剪映草稿名</Form.Label>
              <Form.Control value={form.draft_name} onChange={(event) => setField('draft_name', event.target.value)} disabled={busy} />
              <Form.Text>必须与扫描主机上的草稿目录名完全一致。</Form.Text>
            </Form.Group>
            <Form.Group>
              <Form.Label>适用视频类型</Form.Label>
              <div className="jianying-check-grid">
                {videoTypes.map((videoType) => (
                  <Form.Check key={videoType.id} id={`jianying-new-video-type-${videoType.id}`} type="checkbox" label={getVideoTypeLabel(videoType)} checked={form.video_type_ids.includes(Number(videoType.id))} onChange={() => toggleVideoType(videoType.id)} disabled={busy} />
                ))}
              </div>
            </Form.Group>
            <Form.Check type="switch" id="jianying-new-template-active" label="创建后启用" checked={form.is_active} onChange={(event) => setField('is_active', event.target.checked)} disabled={busy} />
            <JianyingTemplatePreviewField
              disabled={busy}
              onFileChange={setPreviewFile}
              onRemove={() => setPreviewFile(null)}
              pendingFile={previewFile}
              previewUrl={null}
              templateName={form.name}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" disabled={busy} onClick={() => setShowCreate(false)}>取消</Button>
          <Button disabled={busy} onClick={createTemplate}>{busy ? <Spinner size="sm" /> : '读取草稿并创建'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(deleteCandidate)} onHide={() => !busy && setDeleteCandidate(null)} centered>
        <Modal.Header closeButton><Modal.Title>删除模板</Modal.Title></Modal.Header>
        <Modal.Body>确认删除模板“{deleteCandidate?.name}”吗？</Modal.Body>
        <Modal.Footer><Button variant="outline-secondary" disabled={busy} onClick={() => setDeleteCandidate(null)}>取消</Button><Button variant="danger" disabled={busy} onClick={removeTemplate}>删除</Button></Modal.Footer>
      </Modal>
    </PageContentLayout>
  );
};

export default JianyingTemplatesPage;
